import { TOKEN_PROGRAM_ID } from "@solana/spl-token";
import { useConnection } from "@solana/wallet-adapter-react";
import type { TransactionInstruction } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import type { StepFarmPosition } from "@stepfinance/farms";
import { useMutation } from "@tanstack/react-query";

import { findOrCreateAccountByMint } from "../../../actions";
import { buildClaimInstructions, getStepRewardProgram, getStepRewardProvider } from "../../../actions/step/reward-pool";
import { confirmOptions } from "../../../constants/transaction";
import { useLanguageContext } from "../../../contexts/language/language";
import { notify } from "../../../utils/notifications";
import { handlePreSendTxnError, sendTransaction } from "../../../utils/transactions";
import { useStepFarms } from "../../farms";
import { useConnectedWallet } from "../../wallet";

export const useWithdrawAndCloseStepFarm = () => {
  const { connection } = useConnection();
  const wallet = useConnectedWallet();
  const { refreshStepFarms } = useStepFarms();
  const {
    currentLanguageData: {
      common,
      pages: {
        dashboard: {
          modules: { farmGrid },
        },
      },
    },
  } = useLanguageContext();

  return useMutation({
    mutationFn: async (stepFarm: StepFarmPosition) => {
      if (!wallet) {
        return;
      }
      const provider = getStepRewardProvider(connection, wallet);
      const program = getStepRewardProgram(provider);

      const { userStake, rewardPool } = stepFarm;

      if (!userStake) {
        throw new Error(farmGrid.notifications.errorUnstaking.message);
      }

      const userAccountPublicKey = userStake.publicKey;
      const poolPublicKey = rewardPool.publicKey;
      const { stakingMint, stakingVault } = rewardPool.account;

      const stepOutAmount = userStake.account.balanceStaked;

      const instructions: TransactionInstruction[] = [];
      const stakingMintAccount = await findOrCreateAccountByMint(
        connection,
        provider.wallet.publicKey,
        provider.wallet.publicKey,
        instructions,
        stakingMint,
      );
      if (!stakingMintAccount) {
        throw new Error(farmGrid.notifications.errorUnstaking.message);
      }

      const [poolSigner] = PublicKey.findProgramAddressSync([poolPublicKey.toBuffer()], program.programId);

      instructions.push(
        program.instruction.unstake(stepOutAmount, {
          accounts: {
            // Stake instance.
            pool: poolPublicKey,
            stakingVault: stakingVault,
            // User.
            user: userAccountPublicKey,
            owner: provider.wallet.publicKey,
            stakeFromAccount: stakingMintAccount,
            // Program signers.
            poolSigner,
            // Misc.
            tokenProgram: TOKEN_PROGRAM_ID,
          },
        }),
      );

      const claimInstructions = await buildClaimInstructions(
        provider,
        program,
        connection,
        rewardPool.account,
        poolPublicKey,
        poolSigner,
        userAccountPublicKey,
      );

      instructions.push(...claimInstructions);

      instructions.push(
        program.instruction.closeUser({
          accounts: {
            pool: poolPublicKey,
            user: userAccountPublicKey,
            owner: provider.wallet.publicKey,
          },
        }),
      );

      return sendTransaction(connection, wallet, instructions, [], true, confirmOptions, true, {
        inProgress: {
          message: common.notifications.transactionSent,
          description: common.notifications.transactionInProgress,
        },
      });
    },
    onSuccess: (tx) => {
      const { message, description } = farmGrid.notifications.successUnstaking;
      notify({
        message,
        description,
        type: "success",
        txId: tx,
      });
    },
    onError: (error: Error) => handlePreSendTxnError(error),
    onSettled: () => refreshStepFarms(),
  });
};
